import { LucideIcon } from "lucide-react";
import { ReactNode } from "react";
import Link from "next/link";
import { Button } from "@/components/shadcn/button";
import { NoxusIcon } from "../shadcn/icons";
import { cn } from "@/lib/utils";

const NoxusEmptyBlock = ({
  title,
  description,
  icon,
  iconComponent,
  button,
  buttonIcon,
  secondaryButton,
  secondaryButtonIcon,
  size = "default",
  isScreenSize = false,
  link,
  linkButton,
  linkButtonIcon,
  onClick,
  secondaryOnClick,
  isLoading,
  buttonVariant = "default",
  iconBackground = false,
  buttonDisabled = false,
  className,
}: {
  title: string;
  description?: string | ReactNode;
  icon?: LucideIcon | NoxusIcon;
  iconComponent?: React.ReactNode;
  button?: string;
  buttonIcon?: LucideIcon | NoxusIcon;
  secondaryButton?: string;
  secondaryButtonIcon?: LucideIcon | NoxusIcon;
  secondaryOnClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  link?: string;
  linkButton?: string;
  linkButtonIcon?: LucideIcon | NoxusIcon;
  isScreenSize?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isLoading?: boolean;
  buttonVariant?: "default" | "outline";
  size?: "default" | "sm" | "xs" | "2xs";
  iconBackground?: boolean;
  buttonDisabled?: boolean;
  className?: string;
}) => {
  const LucideIcon = icon;
  const IconComponent = iconComponent;
  const ButtonIcon = buttonIcon;
  const SecondaryButtonIcon = secondaryButtonIcon;
  const LinkButtonIcon = linkButtonIcon;

  if (size === "xs" || size === "2xs") {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-3 py-12 px-6 w-full h-full text-center",
          size === "2xs" ? "py-6 px-3" : undefined
        )}
      >
        {LucideIcon && (
          <LucideIcon
            size={size === "2xs" ? 24 : 32}
            strokeWidth={1}
            className="text-foreground"
          />
        )}
        {IconComponent && IconComponent}
        <p
          className={cn(
            "text-base font-medium text-foreground leading-none",
            size === "2xs" ? "text-sm font-normal" : undefined
          )}
        >
          {title}
        </p>
      </div>
    );
  }

  const iconSize = size === "sm" ? 28 : iconBackground ? 24 : 48;
  const iconStrokeWidth = size === "sm" ? 1 : iconBackground ? 1.75 : 1;
  const titleClassName =
    size === "sm"
      ? "text-base font-medium text-foreground leading-none mt-1"
      : "text-xl font-semibold text-foreground leading-none";
  const descriptionClassName =
    size === "sm"
      ? "text-xs text-muted-foreground max-w-sm text-center text-balance leading-relaxed"
      : "text-sm text-muted-foreground max-w-[500px] text-center text-balance";

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center gap-4 p-12 text-muted-foreground",
        isScreenSize ? "w-screen h-screen" : "h-full w-full",
        className
      )}
    >
      <div
        className={
          size === "sm"
            ? undefined
            : iconBackground
              ? "w-10 h-10 flex items-center justify-center bg-accent rounded-xl"
              : undefined
        }
      >
        {LucideIcon && (
          <LucideIcon size={32} strokeWidth={1} className="text-foreground" />
        )}
        {IconComponent && IconComponent}
      </div>

      <p className={titleClassName}>{title}</p>

      {description && (
        <p className={cn("max-w-sm", descriptionClassName)}>{description}</p>
      )}

      {(secondaryButton || button) && (
        <div className="flex gap-3 text-foreground mt-2">
          {secondaryButton && (
            <Button
              onClick={secondaryOnClick}
              variant="outline"
              loading={isLoading}
            >
              {SecondaryButtonIcon && (
                <SecondaryButtonIcon size={20} strokeWidth={1.25} />
              )}
              {secondaryButton}
            </Button>
          )}
          {button && (
            <Button
              variant={buttonVariant}
              onClick={onClick}
              loading={isLoading}
              disabled={buttonDisabled}
            >
              {ButtonIcon && <ButtonIcon size={20} strokeWidth={1.25} />}
              {button}
            </Button>
          )}
        </div>
      )}

      {link && (
        <Link href={link}>
          <Button
            variant={buttonVariant}
            className={
              buttonVariant === "outline" ? "text-foreground" : undefined
            }
          >
            {linkButton}
            {LinkButtonIcon && <LinkButtonIcon size={16} strokeWidth={1.5} />}
          </Button>
        </Link>
      )}
    </div>
  );
};

export { NoxusEmptyBlock };
